import { Link } from "@tanstack/react-router";
import { Skeleton } from "@/components/kit";
import type { CustomerTopResultDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { ReportMobileCard, ReportStat } from "./ReportProductLink";

type CustomerTopTableProps = {
  result?: CustomerTopResultDTO;
  isLoading?: boolean;
};

function formatLastOrderAt(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("lo-LA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function CustomerTypeBadge({
  isNew,
  isReturning,
}: {
  isNew: boolean;
  isReturning: boolean;
}) {
  if (isNew) {
    return (
      <span className="rounded-full bg-accent/50 px-2 py-0.5 text-xs">
        ໃໝ່
      </span>
    );
  }
  if (isReturning) {
    return (
      <span className="rounded-full bg-secondary/15 px-2 py-0.5 text-secondary text-xs">
        ຊື້ຊ້ຳ
      </span>
    );
  }
  return null;
}

function CustomerTopMobileCard({
  row,
}: {
  row: CustomerTopResultDTO["data"][number];
}) {
  return (
    <ReportMobileCard>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to="/app/customers/$id"
            params={{ id: row.customerId }}
            className="font-medium text-secondary hover:underline"
          >
            {row.name}
          </Link>
          <div className="text-muted-foreground text-xs">
            {[row.phone, row.province, row.district].filter(Boolean).join(" · ") ||
              "—"}
          </div>
        </div>
        <CustomerTypeBadge
          isNew={row.isNewInPeriod}
          isReturning={row.isReturningInPeriod}
        />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat label="ອໍເດີ້" value={row.orderCount} />
        <ReportStat label="ຍອດ" value={formatLak(row.revenue)} />
        <ReportStat
          label="ຊື້ລ່າສຸດ"
          value={formatLastOrderAt(row.lastOrderAt)}
          className="col-span-2"
        />
      </dl>
    </ReportMobileCard>
  );
}

export function CustomerTopTable({
  result,
  isLoading,
}: CustomerTopTableProps) {
  const rows = result?.data ?? [];

  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-2xl" />;
  }

  return (
    <section className="brand-card overflow-hidden">
      <div className="flex flex-col gap-1 p-4 sm:p-5">
        <h2 className="font-display text-lg">ລູກຄ້າອັນດັບຕົ້ນ</h2>
        <p className="text-muted-foreground text-sm">
          ຈັດອັນດັບຕາມຍອດຂາຍຢືນຢັນໃນຊ່ວງ — ຄລິກເພື່ອເບິ່ງໂປຣໄຟລ໌
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="border-secondary/15 border-t p-8 text-center text-muted-foreground text-sm">
          ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 border-secondary/15 border-t p-4 md:hidden">
            {rows.map((row) => (
              <CustomerTopMobileCard key={row.customerId} row={row} />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs">
                <tr>
                  <th className="px-4 py-3 font-medium">ລູກຄ້າ</th>
                  <th className="px-4 py-3 font-medium">ພື້ນທີ່</th>
                  <th className="px-4 py-3 font-medium text-right">ອໍເດີ້</th>
                  <th className="px-4 py-3 font-medium text-right">ຍອດ</th>
                  <th className="px-4 py-3 font-medium">ຊື້ລ່າສຸດ</th>
                  <th className="px-4 py-3 font-medium">ປະເພດ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.customerId}
                    className="border-secondary/10 border-t"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to="/app/customers/$id"
                        params={{ id: row.customerId }}
                        className="font-medium text-secondary hover:underline"
                      >
                        {row.name}
                      </Link>
                      {row.phone ? (
                        <div className="text-muted-foreground text-xs">
                          {row.phone}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {[row.province, row.district].filter(Boolean).join(" / ") ||
                        "—"}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.orderCount}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatLak(row.revenue)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {formatLastOrderAt(row.lastOrderAt)}
                    </td>
                    <td className="px-4 py-3">
                      <CustomerTypeBadge
                        isNew={row.isNewInPeriod}
                        isReturning={row.isReturningInPeriod}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
